import { RegistrationFlow } from "@ory/client-fetch";
import { Registration } from "@ory/elements-react/theme";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createSdkErrorHandler, oryConfig, sdk } from "./ory-sdk";

export const Signup = () => {
  const [flow, setFlow] = useState<RegistrationFlow | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const returnTo = searchParams.get("return_to");
  const loginChallenge = searchParams.get("login_challenge");

  const navigate = useNavigate();

  const getFlow = useCallback(
    async (flowId: string) => {
      const flowData = await sdk.getRegistrationFlow({ id: flowId });
      setFlow(flowData);
    },
    [setFlow]
  );

  const sdkErrorHandler = createSdkErrorHandler(
    navigate,
    getFlow,
    setFlow,
    "/signup",
    true
  );

  const createFlow = async () => {
    try {
      const flowData = await sdk.createBrowserRegistrationFlow({
        returnTo: returnTo || window.location.origin,
        ...(loginChallenge && { loginChallenge: loginChallenge }),
      });
      const params: Record<string, string> = { flow: flowData.id };
      if (returnTo) params.return_to = returnTo;
      if (loginChallenge) params.login_challenge = loginChallenge;
      setSearchParams(params);
      setFlow(flowData);
    } catch (error) {
      console.error("Error creating registration flow:", error);
      await sdkErrorHandler(error);
    }
  };

  // Create-or-load the flow exactly once. `createFlow` mints a new
  // registration flow (and a fresh CSRF cookie) on every call, so
  // React's `StrictMode`, which mounts and runs this effect twice in
  // development, would otherwise create two flows: the second's CSRF
  // cookie would then no longer match the first flow this form renders
  // and submits, and Ory would reject the submit as a CSRF violation.
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) {
      return;
    }
    initialized.current = true;

    const flowId = searchParams.get("flow");
    if (flowId) {
      getFlow(flowId).catch(() => createFlow());
      return;
    }
    createFlow();
  }, []);

  if (!flow) {
    return (
      <div className="h-[576px] px-12 pt-12">Loading authorization...</div>
    );
  }

  if (!flow.ui || !flow.ui.nodes) {
    console.error("Flow object is missing ui.nodes:", flow);
    return (
      <div className="h-[576px] px-12 pt-12 text-red-500">
        Error: Invalid flow object. Check console for details.
      </div>
    );
  }

  return (
    <div className="py-0">
      <Registration
        flow={flow}
        config={oryConfig}
        components={{
          Card: {
            Footer: () => (
              <div className="flex flex-col gap-2 text-sm mt-4">
                <button
                  type="button"
                  onClick={() => {
                    const search = new URLSearchParams();
                    if (flow.return_to) search.set("return_to", flow.return_to);
                    if (flow.oauth2_login_challenge)
                      search.set(
                        "login_challenge",
                        flow.oauth2_login_challenge
                      );
                    navigate(
                      { pathname: "/login", search: search.toString() },
                      { replace: true }
                    );
                  }}
                  className="text-primary hover:underline"
                >
                  Already have an account? Sign in
                </button>
              </div>
            ),
          },
        }}
      />
    </div>
  );
};
